import React, { useEffect, useState } from "react";
 import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from "../../components/ui/pagination";
import { Button } from "../../components/ui/button";
import { CiSearch } from "react-icons/ci";
import { Input } from "../../components/ui/input";
import WaterLoader from "../../components/Loader/WaterLoader";
import arrow from "../../images/Arrow 13.png";
import { getAllPaginatedProjects } from "../../API/Projects/projectsService";

interface ProjectsItem {
  title: string;
  date_debut: string;
  description: string;
  imageUrl: string;
  linkUrl: string;
  slug: string;
}

interface ProjectsCardProps {
  title: string;
  date: string;
  description: string;
  imageUrl: string;
  linkUrl: string;
  slug: string;
}

const ProjectsCard: React.FC<ProjectsCardProps> = ({
  title,
  date,
  description,
  imageUrl,
  slug,
}) => {
  const uploadUrl = process.env.REACT_APP_FILE_UPLOAD || "";

  return (
    <div className="max-w-sm w-full md:w-80 m-4 overflow-hidden bg-white rounded-lg shadow-lg h-[300px] flex flex-col">
      <img
        className="object-cover w-full h-48"
        src={`${uploadUrl}/news/${imageUrl}`}
        alt={title}
      />
      <div className="flex-grow px-6 py-4">
        <div className="mb-2 font-bold line-clamp-2">{title}</div>
        <div
          className="w-full text-sm line-clamp-3"
          dangerouslySetInnerHTML={{
            __html:
              description.substring(0, 80).replace(/<\/?strong>/g, "") +
              (description.length > 80 ? "..." : ""),
          }}
        />
      </div>
      <div className="flex items-center justify-between p-2">
        <span className="text-sm text-primary sm:text-base">
          {date
          ? new Date(date + "T00:00:00").toLocaleDateString("fr-FR", {
              day: "numeric",
              month: "long",
              year: "numeric",
            })
          : "-"}

        </span>
        <div className="flex items-center justify-between">
          <a
            href={`/travaux/${slug}`}
            className="text-sm font-semibold transition-colors duration-200 text-primary sm:text-base "
          >
            <div className="flex items-center gap-2">
              Lire plus
              <img src={arrow} alt="" />
            </div>
          </a>
        </div>
      </div>
    </div>
  );
};

const ListTravaux: React.FC = () => {
  const [projects, setProjects] = useState<ProjectsItem[]>([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [keyword, setKeyword] = useState("");
  const [loading, setLoading] = useState(true);

  const fetchData = async () => {
    try {
      setLoading(true);
      const response = await getAllPaginatedProjects(page, keyword);
      if (response) {
        setProjects(response.projects || []);
        setTotalPages(response.totalPages || 1);
      }
    } catch (error) {
      console.error("Failed to fetch news:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [page]);

  const handleNextPageChange = () => {
    if (page < totalPages) {
      setPage(page + 1);
    }
  };

  const handlePreviousPageChange = () => {
    if (page > 1) {
      setPage(page - 1);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setKeyword(e.target.value);
    setPage(1); // Reset to first page when search changes
  };

  const handleSearchClick = () => {
    setPage(1); // Reset to first page on search
    fetchData();
  };

  return (
    <div className="container p-8 mx-auto">
      <h1 className="mb-4 text-2xl font-bold text-center text-black">
        Travaux En cours
      </h1>
      <h2 className="text-center">
        Retrouvez ici les dernières informations, événements et projets en
        cours.
      </h2>
      <div className="relative mx-auto w-[600px] mt-20">
        <div className="relative flex items-center mt-8">
          <div className="absolute pointer-events-none left-3">
            <CiSearch className="w-5 h-5 text-black" />
          </div>
          <Input
            className="py-3 pl-12 pr-12 bg-white border border-primary text-primary h-9 rounded-3xl focus:outline-none focus:border-primary"
            placeholder="Rechercher des travaux..."
            value={keyword}
            onChange={handleInputChange}
          />
          <Button
            className="absolute top-0 bottom-0 right-0 px-6 py-3 text-white rounded-r-md bg-primary rounded-3xl"
            onClick={handleSearchClick}
          >
            Rechercher
          </Button>
        </div>
      </div>

      {loading ? (
        <WaterLoader />
      ) : (
        <>
          <div className="grid grid-cols-1 gap-4 mt-8 md:grid-cols-3">
            {projects.map((item, index) => (
              <ProjectsCard
                key={index}
                title={item.title}
                date={item.date_debut}
                description={item.description}
                imageUrl={item.imageUrl}
                linkUrl={item.linkUrl}
                slug={item.slug}
              />
            ))}
          </div>
          <div className="flex justify-center">
            <Pagination>
              <PaginationContent>
                <PaginationItem>
                  <PaginationPrevious
                    className="cursor-pointer hover:bg-transparent"
                    onClick={handlePreviousPageChange}
                  />
                </PaginationItem>
                {[...Array(totalPages)].map((_, index) => (
                  <PaginationItem key={index}>
                    <PaginationLink
                      className="cursor-pointer hover:bg-transparent"
                      onClick={() => setPage(index + 1)}
                    >
                      <Button
                        variant="outline"
                        size="icon"
                        className="w-10 h-10 text-white rounded-full bg-primary"
                      >
                        {index + 1}
                      </Button>
                    </PaginationLink>
                  </PaginationItem>
                ))}
                <PaginationItem>
                  <PaginationNext
                    onClick={handleNextPageChange}
                    className="cursor-pointer hover:bg-transparent text-primary"
                  />
                </PaginationItem>
              </PaginationContent>
            </Pagination>
          </div>
        </>
      )}
    </div>
  );
};

export default ListTravaux;
